import {
  civilDateFromLocalDate,
  hhmmToMinutes,
  isValidHhmm,
  listClinicCivilDates,
  parseCivilDate,
  type HourPeriod,
} from '@/lib/clinicHours';
import { WORKING_HOURS_DAY_KEYS } from '@/utils/appointmentCalendarDates';
import type { WorkingHours } from '@/hooks/useClinicUsers';

const FALLBACK_WORKING_HOURS: WorkingHours = {
  monday: { enabled: true, start: '09:00', end: '17:00' },
  tuesday: { enabled: true, start: '09:00', end: '17:00' },
  wednesday: { enabled: true, start: '09:00', end: '17:00' },
  thursday: { enabled: true, start: '09:00', end: '17:00' },
  friday: { enabled: true, start: '09:00', end: '17:00' },
  saturday: { enabled: false, start: '09:00', end: '17:00' },
  sunday: { enabled: false, start: '09:00', end: '17:00' },
};

function readWorkingHours(value: unknown): WorkingHours {
  const merged: WorkingHours = { ...FALLBACK_WORKING_HOURS };
  if (!value || typeof value !== 'object') return merged;
  const src = value as Record<string, unknown>;
  (Object.keys(merged) as Array<keyof WorkingHours>).forEach((day) => {
    const entry = src[day];
    if (entry && typeof entry === 'object') {
      const rec = entry as Record<string, unknown>;
      merged[day] = {
        enabled: typeof rec.enabled === 'boolean' ? rec.enabled : merged[day].enabled,
        start: typeof rec.start === 'string' ? rec.start : merged[day].start,
        end: typeof rec.end === 'string' ? rec.end : merged[day].end,
      };
    }
  });
  return merged;
}

export type MemberExceptionType =
  | 'unavailable'
  | 'custom_hours'
  | 'meeting'
  | 'vacation'
  | 'sick';

export type PractitionerHoursSource = 'weekly' | 'exception';

export interface MemberScheduleException {
  id: string;
  membership_id: string;
  clinic_id?: string;
  exception_type: MemberExceptionType;
  starts_on: string;
  ends_on: string;
  starts_time: string | null;
  ends_time: string | null;
  updated_at?: string | null;
  created_at?: string | null;
}

export interface ResolvedPractitionerDayHours {
  enabled: boolean;
  periods: HourPeriod[];
  source: PractitionerHoursSource;
  exceptionId: string | null;
  exceptionType: MemberExceptionType | null;
}

const OFF_TYPES = new Set<MemberExceptionType>(['unavailable', 'meeting', 'vacation', 'sick']);

export function normalizeHhmm(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  const match = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || hours > 23 || minutes > 59) {
    return null;
  }
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export function minutesToHhmm(total: number): string {
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

export function exceptionCoversCivilDate(
  exception: Pick<MemberScheduleException, 'starts_on' | 'ends_on'>,
  isoDate: string,
): boolean {
  return exception.starts_on <= isoDate && isoDate <= exception.ends_on;
}

export function exceptionsForMembershipOnDate(
  exceptions: MemberScheduleException[],
  membershipId: string,
  isoDate: string,
): MemberScheduleException[] {
  return exceptions
    .filter((row) => row.membership_id === membershipId && exceptionCoversCivilDate(row, isoDate))
    .sort((a, b) => {
      const updated = (b.updated_at ?? '').localeCompare(a.updated_at ?? '');
      if (updated !== 0) return updated;
      return (b.created_at ?? b.id).localeCompare(a.created_at ?? a.id);
    });
}

export function weeklyPractitionerPeriods(
  workingHours: WorkingHours,
  date: Date,
): HourPeriod[] {
  const day = workingHours[WORKING_HOURS_DAY_KEYS[date.getDay()]];
  if (!day?.enabled) return [];
  const start = normalizeHhmm(day.start);
  const end = normalizeHhmm(day.end);
  if (!start || !end || !isValidHhmm(start) || !isValidHhmm(end)) return [];
  if (hhmmToMinutes(end) <= hhmmToMinutes(start)) return [];
  return [{ start, end }];
}

export function subtractPeriods(base: HourPeriod[], masks: HourPeriod[]): HourPeriod[] {
  let remaining = base.map((period) => ({ ...period }));
  for (const mask of masks) {
    const maskStart = hhmmToMinutes(mask.start);
    const maskEnd = hhmmToMinutes(mask.end);
    if (maskEnd <= maskStart) continue;
    const next: HourPeriod[] = [];
    for (const period of remaining) {
      const start = hhmmToMinutes(period.start);
      const end = hhmmToMinutes(period.end);
      if (maskEnd <= start || maskStart >= end) {
        next.push(period);
        continue;
      }
      if (start < maskStart) {
        next.push({ start: period.start, end: minutesToHhmm(maskStart) });
      }
      if (end > maskEnd) {
        next.push({ start: minutesToHhmm(maskEnd), end: period.end });
      }
    }
    remaining = next;
  }
  return remaining.filter((period) => hhmmToMinutes(period.end) > hhmmToMinutes(period.start));
}

export function resolvePractitionerDayHours(input: {
  workingHours: WorkingHours | unknown;
  exceptions?: MemberScheduleException[];
  membershipId?: string | null;
  date: Date | string;
}): ResolvedPractitionerDayHours {
  const date = typeof input.date === 'string' ? parseLocalCivilDate(input.date) : input.date;
  const isoDate = typeof input.date === 'string' ? input.date : civilDateFromLocalDate(date);
  const workingHours = readWorkingHours(input.workingHours);
  const covering =
    input.membershipId && input.exceptions
      ? exceptionsForMembershipOnDate(input.exceptions, input.membershipId, isoDate)
      : (input.exceptions ?? []).filter((row) => exceptionCoversCivilDate(row, isoDate));

  const fullDayOff = covering.find((row) => OFF_TYPES.has(row.exception_type) && !row.starts_time && !row.ends_time);
  if (fullDayOff) {
    return {
      enabled: false,
      periods: [],
      source: 'exception',
      exceptionId: fullDayOff.id,
      exceptionType: fullDayOff.exception_type,
    };
  }

  const custom = covering.find((row) => row.exception_type === 'custom_hours');
  let periods = weeklyPractitionerPeriods(workingHours, date);
  let source: PractitionerHoursSource = 'weekly';
  let exceptionId: string | null = null;
  let exceptionType: MemberExceptionType | null = null;

  if (custom) {
    const start = normalizeHhmm(custom.starts_time);
    const end = normalizeHhmm(custom.ends_time);
    periods = start && end && hhmmToMinutes(end) > hhmmToMinutes(start) ? [{ start, end }] : [];
    source = 'exception';
    exceptionId = custom.id;
    exceptionType = 'custom_hours';
  }

  const masks = covering
    .filter((row) => OFF_TYPES.has(row.exception_type) && row.starts_time && row.ends_time)
    .map((row) => ({
      start: normalizeHhmm(row.starts_time) ?? '',
      end: normalizeHhmm(row.ends_time) ?? '',
    }))
    .filter((period) => period.start && period.end);

  if (masks.length > 0) {
    periods = subtractPeriods(periods, masks);
    source = 'exception';
    exceptionId = exceptionId ?? covering.find((row) => OFF_TYPES.has(row.exception_type))?.id ?? null;
    exceptionType = exceptionType ?? covering.find((row) => OFF_TYPES.has(row.exception_type))?.exception_type ?? null;
  }

  return {
    enabled: periods.length > 0,
    periods,
    source,
    exceptionId,
    exceptionType,
  };
}

export function resolvedPractitionerToCalendarHours(resolved: ResolvedPractitionerDayHours): {
  enabled: boolean;
  start: string;
  end: string;
  periods: HourPeriod[];
  source: PractitionerHoursSource;
  exceptionType: MemberExceptionType | null;
} {
  return {
    enabled: resolved.enabled,
    start: resolved.periods[0]?.start ?? '09:00',
    end: resolved.periods[resolved.periods.length - 1]?.end ?? '17:00',
    periods: resolved.periods,
    source: resolved.source,
    exceptionType: resolved.exceptionType,
  };
}

export function intervalFitsPractitionerHours(
  resolved: ResolvedPractitionerDayHours,
  start: Date,
  end: Date,
): boolean {
  if (civilDateFromLocalDate(start) !== civilDateFromLocalDate(end)) return false;
  if (!resolved.enabled || end <= start) return false;
  const startMin = start.getHours() * 60 + start.getMinutes();
  const endMin = end.getHours() * 60 + end.getMinutes() + (end.getSeconds() > 0 ? 1 : 0);
  return resolved.periods.some(
    (period) => startMin >= hhmmToMinutes(period.start) && endMin <= hhmmToMinutes(period.end),
  );
}

export function defaultPractitionerWorkingHours(): WorkingHours {
  return { ...FALLBACK_WORKING_HOURS };
}

export function memberExceptionsFromClinicRows(
  rows:
    | Array<{
        id: string;
        membershipId: string;
        exceptionType: string;
        startsOn: string;
        endsOn: string;
        startsTime: string | null;
        endsTime: string | null;
      }>
    | undefined,
): MemberScheduleException[] {
  return (rows ?? []).map((row) => ({
    id: row.id,
    membership_id: row.membershipId,
    exception_type: row.exceptionType as MemberExceptionType,
    starts_on: row.startsOn,
    ends_on: row.endsOn,
    starts_time: row.startsTime,
    ends_time: row.endsTime,
  }));
}

export function buildPractitionerColumnHours(input: {
  days: Date[];
  members: Array<{ membership_id: string; user_id: string; working_hours: WorkingHours }>;
  exceptions: MemberScheduleException[];
  columnUserIds: string[];
}): Map<string, Map<string, ReturnType<typeof resolvedPractitionerToCalendarHours>>> {
  const memberByUserId = new Map(input.members.map((member) => [member.user_id, member]));
  const byDate = new Map<string, Map<string, ReturnType<typeof resolvedPractitionerToCalendarHours>>>();
  for (const day of input.days) {
    const iso = civilDateFromLocalDate(day);
    const byColumn = new Map<string, ReturnType<typeof resolvedPractitionerToCalendarHours>>();
    for (const userId of input.columnUserIds) {
      const member = memberByUserId.get(userId);
      if (!member) continue;
      byColumn.set(
        userId,
        resolvedPractitionerToCalendarHours(
          resolvePractitionerDayHours({
            workingHours: member.working_hours,
            exceptions: input.exceptions,
            membershipId: member.membership_id,
            date: day,
          }),
        ),
      );
    }
    byDate.set(iso, byColumn);
  }
  return byDate;
}

function parseLocalCivilDate(isoDate: string): Date {
  const [year, month, day] = isoDate.split('-').map(Number);
  const parsed = new Date(year, month - 1, day);
  parsed.setHours(0, 0, 0, 0);
  return parsed;
}

export function listBookableDatesFromPublicClinicians(input: {
  clinicians: Array<{
    membership_id?: string;
    working_hours?: unknown;
  }>;
  exceptions?: Array<Omit<MemberScheduleException, 'exception_type'> & { exception_type: string }>;
  advanceDays: number;
  now?: Date;
}): Date[] {
  return listBookablePractitionerDates({
    practitioners: input.clinicians.map((clinician) => ({
      membershipId: clinician.membership_id,
      workingHours: clinician.working_hours ?? {},
    })),
    exceptions: input.exceptions as MemberScheduleException[] | undefined,
    advanceDays: input.advanceDays,
    now: input.now,
  });
}

export function listBookablePractitionerDates(input: {
  practitioners: Array<{
    membershipId?: string | null;
    workingHours: WorkingHours | unknown;
  }>;
  exceptions?: MemberScheduleException[];
  advanceDays: number;
  now?: Date;
  limit?: number;
}): Date[] {
  const dates: Date[] = [];
  const limit = input.limit ?? 90;
  if (input.practitioners.length === 0) return dates;
  for (const iso of listClinicCivilDates(input.advanceDays, input.now)) {
    const open = input.practitioners.some((practitioner) => {
      const resolved = resolvePractitionerDayHours({
        workingHours: practitioner.workingHours,
        exceptions: input.exceptions,
        membershipId: practitioner.membershipId,
        date: iso,
      });
      return resolved.enabled && resolved.periods.length > 0;
    });
    if (!open) continue;
    dates.push(parseCivilDate(iso));
    if (dates.length >= limit) break;
  }
  return dates;
}
